<template>
    <Head :title="t('chat.tutorial.pageTitle')" />
    <BaseLayout gap="md" content-class="relative">
        <div
            v-for="(depth, index) in scrollDepthMarkers"
            :key="depth"
            ref="markerRefs"
            :data-scroll-depth="depth"
            :class="['pointer-events-none absolute inset-x-0 h-0', markerClasses[index]]"
            aria-hidden="true" />

        <header class="flex flex-col gap-2">
            <h1 class="display-serif text-ink">{{ t("chat.tutorial.heading") }}</h1>
            <p class="body max-w-2xl text-ink-muted">{{ t("chat.tutorial.intro") }}</p>
        </header>

        <section class="flex flex-col gap-4">
            <BaseCard
                v-if="featuredVideo"
                as="button"
                type="button"
                padding="none"
                radius="lgr"
                class="intro-video-card w-full overflow-hidden text-left"
                :aria-label="t('chat.tutorial.videoCardLabel', { title: featuredVideo.title, duration: formatDuration(featuredVideo.durationSeconds) })"
                @click="openVideo(featuredVideo, 0)">
                <div class="relative">
                    <img :src="featuredVideo.poster" alt="" class="aspect-video w-full object-cover" />
                    <div class="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-ink/90 via-ink/35 to-transparent px-3 pb-3 pt-10 sm:px-6 sm:pb-6">
                        <span
                            class="tutorial-video-play-indicator flex size-12 items-center justify-center rounded-full bg-ink text-ink-inverse transition-colors duration-150"
                            aria-hidden="true">
                            <i class="bi bi-play-fill text-[18px]" aria-hidden="true"></i>
                        </span>
                        <span class="rounded-full bg-ink px-3 py-2 body-small-semibold text-ink-inverse" aria-hidden="true">
                            {{ formatDuration(featuredVideo.durationSeconds) }}
                        </span>
                    </div>
                </div>
            </BaseCard>
            <div v-if="featuredVideo" class="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex flex-col gap-1">
                    <p class="title-body-bold text-ink">{{ featuredVideo.title }}</p>
                    <p class="body-small text-ink-muted">{{ t("chat.tutorial.startHere") }}</p>
                </div>
                <BaseButton theme="primary" class="bg-ink text-ink-inverse" @click="openVideo(featuredVideo, 0)">
                    <span class="inline-flex items-center gap-2">
                        <i class="bi bi-play-fill" aria-hidden="true"></i>
                        {{ t("chat.tutorial.watch") }}
                    </span>
                </BaseButton>
            </div>
        </section>

        <section v-for="group in videosByCategory" :key="group.category.id" class="flex flex-col gap-4">
            <h2 class="title-body text-ink">{{ group.category.title }}</h2>
            <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <WalkthroughVideoCard v-for="video in group.videos" :key="video.id" :video="video" @select="openVideo(video, videoPosition(video))" />
            </div>
        </section>

        <section class="flex flex-col gap-4">
            <h2 class="title-body text-ink">{{ t("chat.tutorial.promptsHeading") }}</h2>
            <div class="flex flex-col gap-3">
                <BaseCard
                    v-for="(prompt, index) in prompts"
                    :key="prompt.id"
                    as="button"
                    type="button"
                    class="flex w-full items-center justify-between gap-4 text-left body text-ink disabled:opacity-60"
                    :disabled="isFetching"
                    @click="startPrompt(prompt, index + 1)">
                    <span>{{ t("chat.tutorial.quotedPrompt", { text: prompt.text }) }}</span>
                    <i class="bi bi-arrow-right text-accent" aria-hidden="true"></i>
                </BaseCard>
            </div>
            <p v-if="chatError" role="alert" class="body text-status-error">{{ t("chat.tutorial.startChatError") }}</p>
        </section>

        <footer class="flex flex-wrap gap-6 border-t border-border pt-5 body-small text-ink-muted">
            <span class="min-w-0">
                {{ t("chat.tutorial.dataQuestion") }}
                <BaseButton type="button" theme="tertiary" size="sm" class="max-w-full whitespace-normal wrap-anywhere text-left" @click="privacyDialogOpen = true">
                    {{ organizationDetails?.privacy_link_label || t("chat.tutorial.readPrivacyNotice") }}
                </BaseButton>
            </span>
            <span>
                {{ t("chat.tutorial.somethingWrong") }}
                <a :href="isInIFrame() ? report_issue_url : globalProperties?.$feedbackMailto" class="base-link">{{ t("chat.tutorial.reportIssue") }}</a>
            </span>
        </footer>
    </BaseLayout>
    <PrivacyNoticeDialog v-model:open="privacyDialogOpen" />
    <WalkthroughVideoDialog v-model:open="videoDialogOpen" :video="activeVideo" :position="activeVideoPosition" />
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import { useNadiaTutorialAnalytics, useNadiaTutorialPageClosed, useNadiaTutorialScrollDepth } from "~vue/composables/useNadiaTutorialAnalytics";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import WalkthroughVideoCard from "~vue/NadiaTutorial/WalkthroughVideoCard.vue";
import WalkthroughVideoDialog from "~vue/NadiaTutorial/WalkthroughVideoDialog.vue";
import { formatDuration, introVideo, resolveVideo, TUTORIAL_CATEGORIES, type WalkthroughVideo, walkthroughVideos } from "~vue/NadiaTutorial/walkthroughVideos";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import { isInIFrame } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE } from "~vue/utils/constants";
import { analyticsSourceFromQuery } from "~vue/utils/logUtils";
import type { ComputedRef } from "vue";
import { computed, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Prompt {
    id: string;
    text: string;
}

interface StartChatResponse {
    redirect_url?: string;
}

interface StartChatPayload {
    chat_source: string;
    initial_message: string;
}

const props = defineProps<{
    start_chat_url: string;
    report_issue_url: string;
    walkthrough_video_ids: string[];
    tutorial_video_base_url: string;
}>();
const { t } = useI18n();

defineOptions({ layout: LeftSidebarLayout });

const globalProperties = inject<GlobalProperties>("globalProperties");
const organizationDetails = inject<ComputedRef<OrganizationDetails>>("organization_details");
const { logTutorialEvent } = useNadiaTutorialAnalytics();
const { markerRefs } = useNadiaTutorialScrollDepth(logTutorialEvent);
useNadiaTutorialPageClosed(logTutorialEvent);
const privacyDialogOpen = ref(false);
const videoDialogOpen = ref(false);
const activeVideo = ref<WalkthroughVideo | null>(null);
const activeVideoPosition = ref(0);
const chatError = ref<string | null>(null);
const chatPayload = ref<StartChatPayload | null>(null);
const prompts = computed<Prompt[]>(() => [{ id: "prepare_conversation", text: t("chat.tutorial.prompts.prepareConversation") }]);

const {
    data: chatResponse,
    error: chatRequestError,
    execute: executeChat,
    isFetching,
} = useFetch(props.start_chat_url, { immediate: false }).post(chatPayload).json<StartChatResponse>();

const baseUrl = props.tutorial_video_base_url.trim();
const featuredVideo = computed(() => (baseUrl ? resolveVideo(introVideo, baseUrl) : null));
const enabledVideos = computed(() =>
    baseUrl ? walkthroughVideos.filter((video) => props.walkthrough_video_ids.includes(video.id)).map((video) => resolveVideo(video, baseUrl)) : [],
);
const videosByCategory = computed(() =>
    TUTORIAL_CATEGORIES.map((category) => ({
        category,
        videos: enabledVideos.value.filter((video) => video.category === category.id),
    })).filter((group) => group.videos.length > 0),
);
const scrollDepthMarkers = [25, 50, 75, 100] as const;
const markerClasses = ["top-[25%]", "top-[50%]", "top-[75%]", "bottom-0"] as const;

function openVideo(video: WalkthroughVideo, position: number) {
    activeVideo.value = video;
    activeVideoPosition.value = position;
    videoDialogOpen.value = true;
}

function videoPosition(video: WalkthroughVideo): number {
    return enabledVideos.value.findIndex((enabledVideo) => enabledVideo.id === video.id) + (featuredVideo.value ? 1 : 0);
}

async function startPrompt(prompt: Prompt, position: number) {
    if (isFetching.value) {
        return;
    }

    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_CTA_CLICKED, { prompt_id: prompt.id, position });
    chatError.value = null;
    chatPayload.value = {
        chat_source: CHATSOURCE.NADIA_TUTORIAL_PAGE,
        initial_message: prompt.text,
    };

    await executeChat();
    if (chatRequestError.value || !chatResponse.value?.redirect_url) {
        chatError.value = t("chat.tutorial.startChatError");
        return;
    }

    if (isInIFrame()) {
        globalThis.location.href = chatResponse.value.redirect_url;
    } else {
        router.visit(chatResponse.value.redirect_url);
    }
}

onMounted(() => {
    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_PAGE_VIEWED, {
        source: analyticsSourceFromQuery("direct"),
    });
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.intro-video-card {
    transition:
        translate 150ms ease,
        box-shadow 150ms ease;
}

.intro-video-card:hover {
    translate: 0 -0.125rem;
    @apply shadow-sm;
}

.intro-video-card:hover .tutorial-video-play-indicator {
    @apply bg-valence-accent;
}

@media (prefers-reduced-motion: reduce) {
    .intro-video-card {
        transition: none;
    }

    .intro-video-card:hover {
        translate: none;
    }
}
</style>
